import { resolveProjectSettings, setProjectSettingOverrides } from "@afar-research/afar";
import type { EnvironmentId, ProjectId } from "@afar-research/contracts";
import { useAtomValue } from "@effect/atom-react";
import { useCallback } from "react";

import { serverEnvironment } from "~/state/server";
import { useAtomCommand } from "~/state/use-atom-command";

/**
 * Whether the project sends attached files and folders straight to Ingest, and
 * a way to turn that on from the composer's attach dialog. Project settings
 * turn it back off (the `alwaysIngestAttachments` row).
 */
export function useProjectAlwaysIngestAttachments(
  environmentId: EnvironmentId | null,
  projectId: ProjectId | null,
): { readonly alwaysIngest: boolean; readonly enableAlwaysIngest: () => void } {
  const settings = useAtomValue(serverEnvironment.configValueAtom(environmentId))?.settings;
  const alwaysIngest =
    settings === undefined || projectId === null
      ? false
      : resolveProjectSettings(settings, projectId).alwaysIngestAttachments;
  const persist = useAtomCommand(serverEnvironment.updateSettings, "project settings update");
  const enableAlwaysIngest = useCallback(() => {
    if (environmentId === null || projectId === null || settings === undefined) return;
    // Only this project's override is written; the others stay as they are.
    void persist({
      environmentId,
      input: {
        patch: {
          projectSettingsOverrides: {
            [projectId]: setProjectSettingOverrides(settings, projectId, {
              alwaysIngestAttachments: true,
            }),
          },
        },
      },
    });
  }, [environmentId, persist, projectId, settings]);
  return { alwaysIngest, enableAlwaysIngest };
}
